import { theme } from '@chakra-ui/react';
import { Theme } from '@nivo/core';
import {
  DatumValue,
  ResponsiveLineCanvas,
  SliceTooltipProps,
} from '@nivo/line';
import React from 'react';

import { CmBox, CmFlex } from '@/components/uiParts';
import { ChartDatum } from '@/domain/models/Chart';
import { KPI } from '@/domain/models/KPI';
import { CurrencyDomainService } from '@/domain/services';

import { YAxisFormat } from '.';

interface Props {
  leftAxisData: ChartDatum;
  leftAxisFormat: YAxisFormat;
  leftYLegend: string;
  rightAxisData: ChartDatum;
  rightAxisFormat: YAxisFormat;
  rightYLegend: string;
}

const leftColor = theme.colors.blue[700];
const rightColor = theme.colors.red[500];

const tooltip = ({ slice }: SliceTooltipProps): JSX.Element => {
  return (
    <CmBox bg="white" px={2} py={3} border="1px solid #ccc">
      <CmBox>{slice.points[0].data.xFormatted}</CmBox>
      {slice.points.map((point) => (
        <CmFlex
          key={point.id}
          color={
            point.serieColor === 'rgba(255, 255, 255, 0)'
              ? leftColor
              : point.serieColor
          }
          py={1}
        >
          <CmBox flex={1} fontWeight="bold">
            {KPI[point.serieId as KPI]}
          </CmBox>
          <CmBox ml={2} color="black">
            {point.serieId === 'totalPrice'
              ? point.data.yFormatted
              : point.data.y}
          </CmBox>
        </CmFlex>
      ))}
    </CmBox>
  );
};

const getColoredAxis = (color: string): Theme => {
  return {
    axis: {
      ticks: {
        line: {
          stroke: color,
        },
        text: { fill: color },
      },
      legend: {
        text: {
          fill: color,
        },
      },
    },
  };
};

const yFormat = (value: DatumValue, axisFormat: YAxisFormat) => {
  switch (axisFormat) {
    case 'currency':
      return CurrencyDomainService.priceNotationFormat(Number(value));
    case 'number':
      return '>-d';
  }
};

// 値が小さいデータを渡したコンポーネントを先に用意し、
// 2つ目にすべてのデータを渡したコンポーネントを作り重ねて表示することで複数軸のLineChartを実現する
// https://github.com/plouc/nivo/issues/172
export const MultiAxisLineChart: React.FC<Props> = ({
  leftAxisData,
  leftAxisFormat,
  leftYLegend,
  rightAxisData,
  rightAxisFormat,
  rightYLegend,
}) => {
  return (
    <CmBox height="100%" width="100%" position="relative">
      <CmBox
        height="100%"
        position="absolute"
        top="0px"
        width="100%"
        z-index={0}
      >
        <ResponsiveLineCanvas
          data={[leftAxisData]}
          colors={leftColor}
          layers={['grid', 'axes', 'lines', 'markers', 'legends']}
          lineWidth={3}
          enablePoints={false}
          axisLeft={{
            legendPosition: 'middle',
            legendOffset: -40,
            tickValues: 5,
            legend: leftYLegend,
          }}
          xScale={{
            type: 'time',
            format: '%Y-%m-%d',
            useUTC: false,
            precision: 'day',
          }}
          xFormat="time:%Y-%m-%d"
          yFormat={(value) => yFormat(value, leftAxisFormat)}
          axisBottom={{
            format: '%Y/%m/%d',
            tickValues: Math.min(5, leftAxisData.data.length),
          }}
          theme={getColoredAxis(leftColor)}
          margin={{ top: 40, right: 70, bottom: 40, left: 50 }}
        />
      </CmBox>
      <CmBox
        height="100%"
        position="absolute"
        top="0px"
        width="100%"
        z-index={4}
      >
        <ResponsiveLineCanvas
          data={[leftAxisData, rightAxisData]}
          colors={['rgba(255, 255, 255, 0)', rightColor]}
          lineWidth={3}
          enablePoints={false}
          margin={{ top: 40, right: 70, bottom: 40, left: 50 }}
          axisRight={{
            legendPosition: 'middle',
            legendOffset: 60,
            tickValues: 5,
            legend: rightYLegend,
            format: (val) => CurrencyDomainService.notationFormat(Number(val)),
          }}
          xScale={{
            type: 'time',
            format: '%Y-%m-%d',
            useUTC: false,
            precision: 'day',
          }}
          xFormat="time:%Y-%m-%d"
          yFormat={(value) => yFormat(value, rightAxisFormat)}
          axisLeft={null}
          axisTop={null}
          enableGridY={false}
          enableGridX={false}
          axisBottom={null}
          theme={getColoredAxis(rightColor)}
          isInteractive={true}
          enableSlices="x"
          sliceTooltip={tooltip}
        />
      </CmBox>
    </CmBox>
  );
};
